import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
  Chip,
} from '@mui/material';
import { CompareArrows } from '@mui/icons-material';

const ComparisonControls = ({
  transcripts,
  selectedA,
  selectedB,
  options,
  onSelectA,
  onSelectB,
  onUpdateOptions,
  onCompare,
  loading,
}) => {
  const handleOptionChange = (optionName) => (event) => {
    onUpdateOptions({
      ...options,
      [optionName]: event.target.checked,
    });
  };

  const canCompare = selectedA && selectedB && selectedA !== selectedB;

  const getTranscriptLabel = (transcript) => {
    const modelName = transcript.llm_model || 'unknown';
    const date = new Date(transcript.created_at).toLocaleString();
    return `${modelName} (${date})`;
  };

  const getModelBadge = (transcript) => {
    if (!transcript) return null;

    const model = transcript.llm_model || transcript.llm_provider;
    let color = 'default';

    if (model.includes('gpt-4o-transcribe-diarize')) {
      color = 'secondary';
    } else if (model.includes('gpt-4o-transcribe')) {
      color = 'primary';
    } else if (model.includes('mini')) {
      color = 'info';
    }

    return <Chip label={model} size="small" color={color} sx={{ ml: 1 }} />;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Select Transcripts to Compare
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl fullWidth sx={{ flex: 1, minWidth: 250 }}>
          <InputLabel>Transcript A</InputLabel>
          <Select
            value={selectedA || ''}
            label="Transcript A"
            onChange={(e) => onSelectA(e.target.value)}
          >
            {transcripts.map((transcript) => (
              <MenuItem
                key={transcript.id}
                value={transcript.id}
                disabled={transcript.id === selectedB || transcript.status !== 'completed'}
              >
                {getTranscriptLabel(transcript)}
                {transcript.status !== 'completed' && ` (${transcript.status})`}
              </MenuItem>
            ))}
          </Select>
          {selectedA && (
            <Box mt={1}>
              {getModelBadge(transcripts.find(t => t.id === selectedA))}
            </Box>
          )}
        </FormControl>

        <FormControl fullWidth sx={{ flex: 1, minWidth: 250 }}>
          <InputLabel>Transcript B</InputLabel>
          <Select
            value={selectedB || ''}
            label="Transcript B"
            onChange={(e) => onSelectB(e.target.value)}
          >
            {transcripts.map((transcript) => (
              <MenuItem
                key={transcript.id}
                value={transcript.id}
                disabled={transcript.id === selectedA || transcript.status !== 'completed'}
              >
                {getTranscriptLabel(transcript)}
                {transcript.status !== 'completed' && ` (${transcript.status})`}
              </MenuItem>
            ))}
          </Select>
          {selectedB && (
            <Box mt={1}>
              {getModelBadge(transcripts.find(t => t.id === selectedB))}
            </Box>
          )}
        </FormControl>
      </Box>

      <Box mb={3}>
        <FormControl fullWidth>
          <InputLabel>Reference for WER Calculation</InputLabel>
          <Select
            value={options.reference_transcript || ''}
            label="Reference for WER Calculation"
            onChange={(e) => onUpdateOptions({ ...options, reference_transcript: e.target.value || null })}
          >
            <MenuItem value="">
              <em>Symmetric (no reference)</em>
            </MenuItem>
            <MenuItem value="a">Transcript A (use as ground truth)</MenuItem>
            <MenuItem value="b">Transcript B (use as ground truth)</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {options.reference_transcript
              ? `WER will be calculated relative to Transcript ${options.reference_transcript.toUpperCase()}`
              : 'Symmetric mode: Agreement % shown, WER calculated relative to longer transcript'}
          </Typography>
        </FormControl>
      </Box>

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
        Normalization Options
      </Typography>

      <FormGroup row>
        <FormControlLabel
          control={
            <Switch
              checked={options.normalize_speaker_labels}
              onChange={handleOptionChange('normalize_speaker_labels')}
            />
          }
          label="Strip speaker labels"
        />
        <FormControlLabel
          control={
            <Switch
              checked={options.normalize_timestamps}
              onChange={handleOptionChange('normalize_timestamps')}
            />
          }
          label="Strip timestamps"
        />
        <FormControlLabel
          control={
            <Switch
              checked={options.normalize_punctuation}
              onChange={handleOptionChange('normalize_punctuation')}
            />
          }
          label="Remove punctuation"
        />
        <FormControlLabel
          control={
            <Switch
              checked={!options.case_sensitive}
              onChange={(e) =>
                onUpdateOptions({
                  ...options,
                  case_sensitive: !e.target.checked,
                })
              }
            />
          }
          label="Case insensitive"
        />
        <FormControlLabel
          control={
            <Switch
              checked={options.expand_contractions}
              onChange={handleOptionChange('expand_contractions')}
            />
          }
          label="Expand contractions"
        />
      </FormGroup>

      <Box mt={3}>
        <Button
          variant="contained"
          size="large"
          startIcon={<CompareArrows />}
          onClick={onCompare}
          disabled={!canCompare || loading}
          fullWidth
        >
          {loading ? 'Comparing...' : 'Compare Transcripts'}
        </Button>
      </Box>

      {!canCompare && selectedA && selectedB && selectedA === selectedB && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          Please select two different transcripts to compare
        </Typography>
      )}
    </Paper>
  );
};

export default ComparisonControls;
